import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the message
    toast.success('ההודעה נשלחה בהצלחה!');
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/assets/tennis-pro-logo.jpg" 
              alt="Tennis Pro Israel Logo" 
              className="w-20 h-20 sm:w-24 sm:h-24 object-contain rounded-xl shadow-lg"
            />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="contact-title">
            צור קשר
          </h1>
          <p className="text-lg text-gray-600">
            נשמח לעמוד לשירותך
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Contact Form */}
          <Card className="glass-effect" data-testid="contact-form-card">
            <CardHeader>
              <CardTitle className="text-2xl">שלח לנו הודעה</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="space-y-2">
                  <Label htmlFor="name">שם מלא</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="שם מלא"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">אימייל</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">הודעה</Label>
                  <Textarea
                    id="message"
                    placeholder="כתוב כאן את ההודעה שלך..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="contact-submit-btn">
                  שלח הודעה
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* Phone Card */}
            <a 
              href="tel:+972544822788" 
              className="block"
              data-testid="phone-link"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="phone-card">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl mb-2 text-gray-900">טלפון</h3>
                      <div className="text-blue-600 hover:text-blue-700 text-lg font-medium">
                        054-4822788
                      </div>
                      <p className="text-gray-600 mt-1">לחץ לחיוג ישיר 📞</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* Email Card */}
            <a 
              href="mailto:Shafirroy@icloud.com?subject=פנייה%20מאתר%20Tennis%20Pro%20Israel"
              className="block"
              data-testid="email-link"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="email-card">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl mb-2 text-gray-900">אימייל</h3>
                      <div className="text-blue-600 hover:text-blue-700 text-lg font-medium break-all">
                        Shafirroy@icloud.com
                      </div>
                      <p className="text-gray-600 mt-1">לחץ לשליחת מייל ישיר ✉️</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* WhatsApp Card */}
            <a 
              href="https://wa.me/972544822788?text=שלום,%20אני%20מעוניין%20לקבל%20מידע%20על%20התחרויות" 
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="whatsapp-link"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Card className="card-hover bg-gradient-to-r from-green-500 to-green-600 cursor-pointer hover:shadow-lg transition-shadow" data-testid="whatsapp-card">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl mb-2 text-white">וואטסאפ</h3>
                      <div className="inline-block mt-2 bg-white text-green-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors">
                        פתח וואטסאפ 💬
                      </div>
                      <p className="text-white text-sm mt-2 opacity-90">לחץ לשיחה ישירה</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* Image */}
            <Card className="overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1714741980848-5b0b77d2cd68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZW5uaXMlMjB0b3VybmFtZW50JTIwYWN0aW9ufGVufDB8fHx8MTc2MDI1Nzk3Nnww&ixlib=rb-4.1.0&q=85"
                alt="Tennis"
                className="w-full h-48 object-cover"
              />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
